import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import Stories from 'react-insta-stories';
import styles from './week-reflection.module.css';
import { getAuth } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { GreetingColors } from '../../Colors';
import TimeContainer from '../../TimeContainer';
import { ComponentMap } from './StoryComponentMap';
import { WeekReviewManager } from '../../helpers/WeekReview/WeekReviewManager';
import { WeekReflectionContext } from './WeekReflectionContext';
import { ShareLinkContext } from '../share/ShareLinkContext';
import ShareModal from '../share/ShareModal';
import { RenewSubscriptionFlow } from '../payments/RenewSubscriptionFlow';


let weekReviewManager = null;
let timeOfDay = new TimeContainer().getTimeOfDay();
const timeOfDayBGColor = GreetingColors[timeOfDay].background;

export const WeekReflection = ({ isUserPaidSubscriber }) => {
    const location = useLocation();
    const auth = getAuth();
    const [user] = useAuthState(auth);

    const [stories, setStories] = useState();
    const [restartCount, setRestartCount] = useState(0);
    const [dynamicStoryData, setDynamicStoryData] = useState(null);
    const [haveStoriesBeenUpdated, setHaveStoriesBeenUpdated] = useState(false);
    const { textInputs, setTextInput, setReflectionId, hasInputChanged,
        setWeekReviewManager, setLastStoryName, registerRestartStoriesFn } = useContext(WeekReflectionContext);
    const textInputsRef = useRef(textInputs); // so the cleanup code can reference the latest value
    const hasInputChangedRef = useRef(hasInputChanged);
    const { showShareModal, shareLink } = useContext(ShareLinkContext);

    const restartStories = () => {
        // this will trigger full remount, starting the stories from the top
        setRestartCount(prev => prev + 1);
    }

    useEffect(() => {
        hasInputChangedRef.current = hasInputChanged;
    }, [hasInputChanged])

    useEffect(() => {
        textInputsRef.current = textInputs;
    }, [textInputs])

    useEffect(async () => {
        weekReviewManager = new WeekReviewManager(user.uid, location.state.currentWeekStart, location.state.flowEntries);
        setWeekReviewManager(weekReviewManager);
        registerRestartStoriesFn(restartStories);

        // check if a reflection was previously stored
        const storedReflection = await weekReviewManager.fetchStoredReflection();
        // If user isn't a paid subscriber and there isn't a previously stored reflection for the week, 
        // then don't show the weekly stories
        if (!isUserPaidSubscriber && (!storedReflection || ('error' in storedReflection))) {
            setStories([{
                content: (props) => <div style={{ position: 'relative', zIndex: 2000 }}>
                    <RenewSubscriptionFlow timeOfDay={timeOfDay} headingType={'weekReflection'} />
                </div>
            }])
            return;
        }

        const storiesData = weekReviewManager.getDataForWeekReview();
        setStories(storiesData.map((storyData, i) => {
            const ComponentToRender = ComponentMap[storyData.component];
            const duration = storyData.duration;
            if (i === storiesData.length - 1) {
                setLastStoryName(storyData.component);
            }
            return {
                content: (props) => <ComponentToRender {...props} storyData={storyData} handleRestart={restartStories} />,
                duration: duration,
                storyData: storyData,
            };
        }));

        // update stories with stored/dynamic data
        const dynamicDataForStories = await weekReviewManager.updateStoriesWithFetchedContent(storedReflection);
        if (dynamicDataForStories.reflectionId) {
            setReflectionId(dynamicDataForStories.reflectionId);
        }
        setDynamicStoryData(dynamicDataForStories);
    }, [location.state.currentWeekStart, isUserPaidSubscriber])

    useEffect(async () => {
        updateStoriesWithDynamicContent();
    }, [dynamicStoryData])

    const updateStoriesWithDynamicContent = async () => {
        if (haveStoriesBeenUpdated || !dynamicStoryData) {
            return;
        }

        let currentStories = [...stories];
        dynamicStoryData.updatedStoriesWithSavedReflections?.forEach(updatedStory => {
            // update the textInput context with these values
            let key = updatedStory.component;
            if (updatedStory.component.includes('recurringCauses')) {
                key = key + updatedStory.index;
            }

            setTextInput(prevInput => ({
                ...prevInput,
                [key]: updatedStory.data.comments ? updatedStory.data.comments : updatedStory.data.answer
            }))
        })

        dynamicStoryData.updatedStoriesWithGptText?.forEach(updatedStory => {
            let storyIndex = currentStories.findIndex(story => story.storyData.component === updatedStory.component);
            if (storyIndex === -1) {
                return
            }

            if (updatedStory.data?.trend?.length > 0) {
                const ComponentToRender = ComponentMap[updatedStory.component];
                currentStories[storyIndex].content = (props) => <ComponentToRender {...props} storyData={updatedStory} />
            }
            else {
                // do nothing if peak valley didn't get the gpt text, coz we will still show the chart
                if (updatedStory.component === 'peakValley') {
                    return;
                }
                // else remove the story
                currentStories.splice(storyIndex, 1);
            }
        });

        setStories(currentStories);
        setHaveStoriesBeenUpdated(true);
    }

    return (
        <div className={styles.storiesScreenBackground}>
            {stories &&
                <Stories
                    key={restartCount}
                    stories={stories}
                    defaultInterval={5000}
                    width={"100%"}
                    height={"85svh"}
                    storyContainerStyles={{
                        borderRadius: 8,
                        maxWidth: "360px",
                        maxHeight: "650px",
                        background: timeOfDayBGColor,
                        color: "white",
                    }}
                    progressContainerStyles={{ overflowX: 'clip' }}
                    onStoryEnd={updateStoriesWithDynamicContent} // checks whether to update stories with gpt data at the end of every story
                    onStoryStart={updateStoriesWithDynamicContent} // we check to update here too to handle the case where a user clicks to the next story 
                />
            }
            {showShareModal && <ShareModal shareUrl={shareLink} timeOfDay={timeOfDay} bgColor={timeOfDayBGColor} />}
        </div>
    )
}